import React, { useEffect, useState } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'

import {
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip
} from 'recharts'
import { useImmer } from 'use-immer'
import ShimmeringEffect from '../../components/shimmering/ShimmeringEffect'
import {
  CustomLabel,
  CustomizedLegend,
  replaceString
} from '../../lib/AllGlobalFunction'
import { isSellerPanel } from '../../lib/AllStaticVariables'
import axiosProvider from '../../lib/AxiosProvider'

const SellerChart = ({ values, setFieldValue }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useImmer({
    seller: {},
    sellerData: [],
    kyc: {},
    kycData: []
  })

  const getSellerColor = (key) => {
    let color = ''
    switch (key) {
      case 'active':
        color = '#641d9f'
        break

      case 'inactive':
        color = '#eee1ff'
        break

      case 'newSellers':
        color = '#a76df2'
        break

      default:
        break
    }
    return color
  }

  const getKycColor = (key) => {
    let color = ''
    switch (key) {
      case 'completed':
        color = '#d6fffd'
        break

      case 'notApproved':
        color = '#35afaa'
        break

      case 'pending':
        color = '#42e1da'
        break

      default:
        break
    }
    return color
  }

  const fetchData = async (days = 'All') => {
    try {
      setLoading(true)
      const sellerResponse = await axiosProvider({
        method: 'GET',
        endpoint: isSellerPanel
          ? 'Seller/Dashboard/getSellerCounts'
          : 'Dashboard/getSellerCounts',
        queryString: `?days=${days}`
      })

      const kycResponse = await axiosProvider({
        method: 'GET',
        endpoint: isSellerPanel
          ? 'Seller/Dashboard/getKycCounts'
          : 'Dashboard/getKycCounts',
        queryString: `?days=${days}`
      })
      setLoading(false)

      if (sellerResponse?.status === 200) {
        let sellerData = Object.entries(sellerResponse?.data?.data).reduce(
          (arr, [key, value]) => {
            if (key !== 'total') {
              arr.push({
                name: replaceString(key),
                value,
                color: getSellerColor(key)
              })
            }
            return arr
          },
          []
        )
        setData((draft) => {
          draft.seller = sellerResponse?.data?.data
          draft.sellerData = sellerData
        })
      }

      if (kycResponse?.status === 200) {
        let kycData = Object.entries(kycResponse?.data?.data).reduce(
          (arr, [key, value]) => {
            if (key !== 'total') {
              arr.push({
                name: replaceString(key),
                value,
                color: getKycColor(key)
              })
            }
            return arr
          },
          []
        )
        setData((draft) => {
          draft.kyc = kycResponse?.data?.data
          draft.kycData = kycData
        })
      }
    } catch {
      setLoading(false)
    }
  }

  const CustomTab = ({ title }) => (
    <div
      onClick={() => {
        setFieldValue('activeTab', title)
      }}
      style={{ cursor: 'pointer', marginRight: '10px' }}
    >
      {title}
    </div>
  )

  useEffect(() => {
    fetchData()
  }, [])

  return loading ? (
    <ShimmeringEffect />
  ) : (
    <div
      style={{ boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px' }}
      className='pv-dashbord-chart-col pv-PieChart-main rounded h-100'
    >
      <Tabs activeKey={values?.activeTab ?? 'Sellers'} id='tabs'>
        <Tab
          eventKey='Sellers'
          title={<CustomTab title='Sellers' />}
          onClick={() => {
            setFieldValue('activeTab', 'Sellers')
          }}
        >
          <ResponsiveContainer
            className='pv-piechart-main'
            width='100%'
            height={140}
          >
            <PieChart>
              <Tooltip />
              <Pie
                data={data?.sellerData}
                dataKey='value'
                cx={'30%'}
                cy={60}
                innerRadius={0}
                outerRadius={100}
              >
                {data?.sellerData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <Label
                  content={
                    <CustomLabel
                      labelText={'Total :'}
                      value={data?.seller?.total}
                    />
                  }
                  position='center'
                />
              </Pie>
              <Legend
                layout='vertical'
                verticalAlign='middle'
                align='left'
                content={<CustomizedLegend />}
              />
            </PieChart>
          </ResponsiveContainer>
        </Tab>
        <Tab
          eventKey='Kyc'
          title={<CustomTab title='Kyc' />}
          onClick={() => {
            setFieldValue('activeTab', 'Kyc')
          }}
        >
          <ResponsiveContainer
            className='pv-piechart-main'
            width='100%'
            height={140}
          >
            <PieChart>
              <Tooltip />
              <Pie
                data={data?.kycData}
                dataKey='value'
                cx={'30%'}
                cy={60}
                innerRadius={0}
                outerRadius={100}
                fill='red'
              >
                {data?.kycData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <Label
                  content={
                    <CustomLabel labelText='Total :' value={data?.kyc?.total} />
                  }
                  position='center'
                />
              </Pie>
              <Legend
                layout='vertical'
                verticalAlign='middle'
                align='left'
                content={<CustomizedLegend />}
              />
            </PieChart>
          </ResponsiveContainer>
        </Tab>
      </Tabs>
      <Dropdown className='pv-chartdash-dropbtn'>
        <Dropdown.Toggle className='btn-light' id='dropdown-basic'>
          {values?.seller ? values?.seller : 'All'}
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item
            onClick={() => {
              fetchData('All')
              setFieldValue('seller', 'All')
            }}
          >
            All
          </Dropdown.Item>
          <Dropdown.Item
            onClick={async () => {
              fetchData('Today')
              setFieldValue('seller', 'Today')
            }}
          >
            Today
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              fetchData('7')
              setFieldValue('seller', 'Last Week')
            }}
          >
            Last Week
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              fetchData('15')
              setFieldValue('seller', 'Last 15 Days')
            }}
          >
            Last 15 Days
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              fetchData('30')
              setFieldValue('seller', 'This Month')
            }}
          >
            This Month
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              fetchData('90')
              setFieldValue('seller', 'Last 3 Months')
            }}
          >
            Last 3 Months
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}

export default SellerChart
